import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useAppDispatch } from '@hooks/index';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { Button }from '@cmsgov/design-system';
import Paginator from '@components/common/paginator/Paginator';
import AlertMessage from '@components/individualPortal/dashboard/AlertMsgs/AlertMessage';
import { useDebounce } from '@containers/common/useDebounce';
import MultiSelectDropdown from '@components/multiSelectDropdown/MultiSelectDropdown';
import SearchInputBox from '@components/searchInputBox/SearchInputBox';
import { showModal, hideModal } from '@actions/common/actionCreators';
import { ticketCategoriesSelector, searchTktCategoriesAndTypesSelector, sortedWorkflowsByCategorySelector, selectedTicketDetailsSelector, ticketCategorySuccessMsgSelector, ticketTypeSuccessMessageSelector, tktCategoryNavigationStateSelector  } from '@selectors/ticketMgmtSelectors';
import { getTicketCategoriesByStatus, updateCategories, getTicketTypesBySearch, clearTktTypesSearchResults, getWorkflowsByCategory, renameCategory, openTicketDrawer, openTicketTypeForm, setCategorySuccessMsg,  setTktTypeDetailsSuccessMsg, showTktCategoryNavigation } from '@actions/ticketMgmtPortal/ticketMgmtPortalActionCreators';
import { CATEGORY_FORM_NAMES, CATEGORY_FORM_FIELDS, ENTITY_TYPES } from '../forms/ticketFormConfigs';
import { ticketMgmtModalTypes } from '@constants/ticketMgmt/modals/ticketMgmtModalTypes';
import { debounceSearchTime, minSearchQueryLength, PAGE_SIZE } from '@constants/common.constants';
import { DEFAULT_TICKET_TYPE_STATUS, TICKET_TYPE_STATUS } from '@constants/ticketMgmt/ticketMgmt';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import ResultDialog from '@components/common/modals/ResultDialog';
import { TicketTypesTable } from './TicketTypesTable';
import TicketTypesDrawer from './TicketTypesDrawer';

const TicketCategories = (props) => {
    const {
        ticketCategories,
        searchTicketTypeResults,
        showCategoryNavigation,
        updateCategories,
        getTicketCategoriesByStatus,
        getTicketTypesBySearch,
        showModal,
        hideModal,
        getWorkflowsByCategory,
        renameCategory,
        workflowsByCategorySelection,
        ticketTypeDetails,
        tktTypeFormMsg,
        tktCategorySuccessMsg
    } = props;
    const [ activeCategoryIndex, setActiveCategoryIndex ] = useState(-1);
    const [ page, updatePage ] = useState(0);
    const [ showListView, setShowListView ] = useState(true);
    const [ selectedStatus, setSelectedStatus ] = useState<string[]>([DEFAULT_TICKET_TYPE_STATUS]);
    const [ selectedStatusValues, setSelectedStatusValues ] = useState('');
    const [ showWarningMsg, setShowWarningMsg ] = useState(false);
    const [ searchTerm, setSearchTerm ] = useState('');
    const dispatch = useAppDispatch();
    const debounceSearchValue: string = useDebounce(searchTerm, debounceSearchTime);
    const totalRecords = ticketCategories.length ? ticketCategories.length : 0;

    useEffect(() => {
        const statusValues = getSelectedStatusValues();
        setSelectedStatusValues(statusValues);
    }, [selectedStatus]);
    
    useEffect(() => {
        if (debounceSearchValue && debounceSearchValue.trim().length >= minSearchQueryLength) {
            getTicketTypesBySearch(selectedStatusValues, debounceSearchValue);
            setShowListView(false);
        }
    }, [debounceSearchValue]);

    useEffect(() => {
        if (!showListView && searchTicketTypeResults?.length && (tktTypeFormMsg || tktCategorySuccessMsg)) {
            onSearchQueryClear();
            dispatch(openTicketTypeForm(false)); 
            dispatch(openTicketDrawer(false));
            dispatch(showTktCategoryNavigation(false));
        }
    }, [showListView, searchTicketTypeResults, tktTypeFormMsg, tktCategorySuccessMsg]);

    useEffect(() => {
        if(tktCategorySuccessMsg) {
            hideModal(ticketMgmtModalTypes.TICKET_TYPE_MODAL);
            setActiveCategoryIndex(-1);
            dispatch(openTicketTypeForm(false)); 
            dispatch(openTicketDrawer(false)); 
        }
    }, [tktCategorySuccessMsg]);
 
    const onSearchQueryChange = (query: string) => {
        setSearchTerm(query);
        if(query.length === 0) {
            setShowListView(true);
            dispatch(clearTktTypesSearchResults());
        }
    };

    useEffect(() => {   
        if(selectedStatusValues !== '') {
            getTicketCategoriesByStatus(selectedStatusValues);
        }
    }, [selectedStatusValues]);

    const processedList = useMemo(() => {
        const startIndex = page * PAGE_SIZE;
        return ticketCategories.slice(startIndex, startIndex + PAGE_SIZE);
    }, [page, ticketCategories]);

    useEffect(() => {
        const shouldFetchWorkflows = activeCategoryIndex > -1 || (showCategoryNavigation && ticketTypeDetails);
        if (shouldFetchWorkflows) {
            const activeCategory = showCategoryNavigation && ticketTypeDetails
                ? ticketTypeDetails?.category
                : processedList[activeCategoryIndex]?.categoryNameEnglish;
    
            if (activeCategory) {
                getWorkflowsByCategory(activeCategory, selectedStatusValues);
            }
        }
    }, [activeCategoryIndex, processedList, ticketTypeDetails, selectedStatusValues, showCategoryNavigation]);
    
    const changePage = newPage => {
        updatePage(newPage);
    };

    const getSelectedStatusValues = () => {
        const selectedValues = selectedStatus.map((statusText) => {
            const statusObj = TICKET_TYPE_STATUS.find((status) => status.text === statusText);
            return statusObj ? statusObj.value : statusText;
        });
        return selectedValues.join(',');
    };

    const handleSelection = (option) => {
        const isSelected = selectedStatus.includes(option);
        if (isSelected && selectedStatus.length === 1) {
            setShowWarningMsg(true);
            return;
        }
        updatePage(0);
        setSelectedStatus(
            isSelected
                ? selectedStatus.filter((item) => item !== option)
                : [...selectedStatus, option]
        );
    };

    const handleCategoryClick = useCallback((index) => {
        setActiveCategoryIndex(index);
        dispatch(openTicketTypeForm(false)); 
        dispatch(openTicketDrawer(true)); 
        dispatch(showTktCategoryNavigation(false));
    }, [dispatch]);

    const removeTag = (option) => {
        if(selectedStatus.length === 1){
            setShowWarningMsg(true);
            return;
        }
        updatePage(0);
        setSelectedStatus(selectedStatus.filter((item) => item !== option));
    };

    const closeMultiSelectWarningModal = () => {
        setShowWarningMsg(false);
    };

    const onSearchQueryClear = () => {
        setSearchTerm('');
        setShowListView(true);
        dispatch(clearTktTypesSearchResults());
    };

    const paginatorData = useMemo(() => ({
        first: page === 0,
        isEmpty: totalRecords === 0,
        last: totalRecords > 0 && page === (Math.ceil(totalRecords / PAGE_SIZE) - 1),
        pageNumber: page,
        totalPages: Math.ceil(totalRecords / PAGE_SIZE)
    }), [page, totalRecords]);

    const handleAddNewCategory = useCallback(() => {
        showModal({
            modalType: ticketMgmtModalTypes.TICKET_TYPE_MODAL,
            modalData: {
                className: 'ticket_management',
                headerText: 'ticket_management.add_new_category',
                initialFormValues: { [CATEGORY_FORM_NAMES.NEW_CATEGORY]: '', [CATEGORY_FORM_NAMES.SPANISH_NAME]: '' },
                formFields: CATEGORY_FORM_FIELDS,
                entity: ENTITY_TYPES.CATEGORY,
                onSaveHandler: ({ newCategory, spanishName  }) => updateCategories({
                    category: newCategory,
                    type: newCategory,
                    description: newCategory,
                    categoryEs: spanishName,
                    typeEs: spanishName
                }, selectedStatusValues)
            }
        });
    }, [selectedStatusValues]);

    const handleRenameCategory = useCallback(() => {
        const categoryItem = processedList[activeCategoryIndex] || ticketTypeDetails;
        const categoryNameEn = categoryItem?.categoryNameEnglish ?? categoryItem?.category;
        const categoryNameEs = categoryItem?.categoryNameSpanish ?? categoryItem?.categoryEs;
        showModal({
            modalType: ticketMgmtModalTypes.TICKET_TYPE_MODAL,
            modalData: {
                className: 'ticket_management',
                headerText: 'ticket_management.rename_category',
                initialFormValues: { [CATEGORY_FORM_NAMES.NEW_CATEGORY]: categoryNameEn, [CATEGORY_FORM_NAMES.SPANISH_NAME]: categoryNameEs },
                formFields: CATEGORY_FORM_FIELDS,
                entity: ENTITY_TYPES.CATEGORY,
                onSaveHandler: ( newVals) => {
                    const { newCategory, spanishName } = newVals;
                    const shouldRename = categoryNameEn !== newCategory || categoryNameEs !== spanishName;
                    if (shouldRename) {
                        return renameCategory(categoryNameEn, newCategory, categoryNameEs, spanishName, selectedStatusValues);
                    }
                }
            }
        });
    }, [activeCategoryIndex, processedList, ticketTypeDetails]);

    const paginator = (ticketCategories.length > PAGE_SIZE) && (<Paginator
        { ...{
            changePage,
            floatCenter: true,
            ...paginatorData
        } }
    />);

    const handlePreviousClick = () => {
        setActiveCategoryIndex( (index) => Math.max(0, index - 1));
    };

    const handleNextClick = () => {
        setActiveCategoryIndex( (index) =>  index + 1);
    };

    const handleCloseTktTypeFormModal = () => {
        dispatch(setTktTypeDetailsSuccessMsg(''));
    };

    const closeTktCategorySuccessModal = () => {
        dispatch(setCategorySuccessMsg(''));
    };
    return (
        <div id="ticketCategories" data-testid="ticket-categories">
            <div id="ticketCategoriesContainer" className="gi-panel" data-testid="ticket-categories-container">
                <div className='ticket-categories__header' data-testid='ticket-categories-header'>
                    <div className='grid-row'>
                        <div className='desktop:grid-col-4' data-testid="search-input-box-container">
                            <SearchInputBox 
                                placeholderId={'ticket_management.ticketTypes.search.placeholder'}
                                searchQuery={searchTerm}
                                handleQueryChange={onSearchQueryChange}
                                handleClear={onSearchQueryClear}
                                aria-label="Search ticket categories and ticket types"
                                data-testid="search-input-box"
                            />
                        </div>
                        <ConditionalRendering isTrue={showListView}>
                            <div className='desktop:grid-offset-1 desktop:grid-col-4' data-testid="multi-select-dropdown-container">
                                <MultiSelectDropdown 
                                    labelId={'ticket.table_title.status'} 
                                    optionsList={TICKET_TYPE_STATUS.map((status) => status.text)}
                                    selectedOptions={selectedStatus} 
                                    handleSelect={handleSelection}
                                    handleTagRemoval={removeTag}
                                    aria-label="Filter ticket categories by status"
                                    data-testid="multi-select-dropdown"
                                />
                            </div>
                            <div className='desktop:grid-col-3' data-testid="add-new-category-button-container">
                                <Button id='addNewTicketCategory' className='usa-button gi-button gi-button__primary ticket__add-category'
                                    onClick={handleAddNewCategory} aria-label="Add new ticket category" data-testid="add-new-category-button">
                                    <FontAwesomeIcon icon={faPlus} className='fa-add-icon' />
                                    <FormattedMessage id="ticket_management.add_new_category"/>
                                </Button>
                            </div>
                        </ConditionalRendering>
                    </div>  
                </div>
                <ConditionalRendering isTrue={showListView}>
                    <div className="ticket-categories-list-container" data-testid="ticket-categories-list-container">  
                        <h3>    
                            <FormattedMessage id="ticket_management.ticket_categories"/>
                        </h3>
                        <ul className='ticket-categories__list' data-testid="ticket-categories-list">
                            {processedList.map((category, index) => (
                                <li key={index} onClick={() => handleCategoryClick(index)} className={index === activeCategoryIndex ? 'active' : '' } aria-label={`Category: ${category.categoryNameEnglish}`} data-testid={`ticket-category-${index}`}>
                                    <span className='category-name' data-testid={`category-name-${index}`}>
                                        {category.categoryNameEnglish}{category.categoryNameSpanish ? ` (${category.categoryNameSpanish})` : ''}
                                    </span>
                                    <span className="count-badge" data-testid={`category-count-${index}`}>{category.count || 0}</span>
                                </li>
                            ))}
                        </ul>
                        
                        <ConditionalRendering isTrue={!ticketCategories?.length}>
                            <div className='empty-ticket-category-container' data-testid="empty-ticket-category-container">
                                <AlertMessage
                                    showIcon={false}
                                    content={<FormattedMessage id='norecordsfound' />}
                                    type="info" aria-live="polite"
                                    data-testid="no-records-alert"/> 
                            </div>
                        </ConditionalRendering>
                    </div>
                </ConditionalRendering>

                <ConditionalRendering isTrue={showListView && ticketCategories && ticketCategories.length > PAGE_SIZE}>
                    <div className='ticket-categories-paginator' data-testid="ticket-categories-paginator">{paginator}</div>
                </ConditionalRendering>
                <ConditionalRendering isTrue={!showListView}>
                    <TicketTypesTable ticketTypesData={searchTicketTypeResults} pageSize={PAGE_SIZE} showCategoryColumn data-testid="ticket-types-table" />
                </ConditionalRendering>
            </div>
            <ConditionalRendering isTrue={showWarningMsg}>
                <ResultDialog message={<FormattedMessage id='ticket_management.ticketTypes.filter.unselected' />} headerId={'common.modal.label.warning'} onClose={closeMultiSelectWarningModal} aria-labelledby="warning-dialog" data-testid="warning-dialog"/>
            </ConditionalRendering>
            <ConditionalRendering isTrue={activeCategoryIndex > -1 || ticketTypeDetails}>
                <TicketTypesDrawer workFlowsByCategory={workflowsByCategorySelection} activeCategoryIdx={activeCategoryIndex} processedList={processedList} onOpenRenameCategoryForm={handleRenameCategory} onPrevClick={handlePreviousClick} onNextClick={handleNextClick} activeCategoryIndexSetter={setActiveCategoryIndex} statusList={selectedStatusValues} data-testid="ticket-types-drawer" />
            </ConditionalRendering>
            <ConditionalRendering isTrue={tktTypeFormMsg}>
                <ResultDialog message={<FormattedMessage id={tktTypeFormMsg}/>} headerId={'common.modal.success'} onClose={handleCloseTktTypeFormModal} aria-labelledby="ticket-type-success-dialog" data-testid="ticket-type-success-dialog"/>
            </ConditionalRendering>
            <ConditionalRendering isTrue={tktCategorySuccessMsg}>
                <ResultDialog message={<FormattedMessage id={tktCategorySuccessMsg}/>} headerId={'common.modal.success'} onClose={closeTktCategorySuccessModal} aria-labelledby="ticket-category-success-dialog" data-testid="ticket-category-success-dialog"/>
            </ConditionalRendering>
        </div>
    );
};

const mapStateToProps = createStructuredSelector({
    ticketCategories: ticketCategoriesSelector,
    searchTicketTypeResults: searchTktCategoriesAndTypesSelector,
    showCategoryNavigation: tktCategoryNavigationStateSelector,
    ticketTypeDetails: selectedTicketDetailsSelector,
    tktTypeFormMsg: ticketTypeSuccessMessageSelector,
    tktCategorySuccessMsg: ticketCategorySuccessMsgSelector,
    workflowsByCategorySelection: sortedWorkflowsByCategorySelector,
});

export default connect(mapStateToProps, { showModal, hideModal, updateCategories, getTicketCategoriesByStatus, getTicketTypesBySearch, getWorkflowsByCategory, renameCategory })(TicketCategories);